import {
  HbrBadge,
  HbrIcon,
  HbrInput,
  HbrSpinner,
  HbrTab,
  HbrTabGroup,
  HbrTabPanel,
} from '@harbor/elements-react-standalone';
import { arrowSquareOut, arrowsOutSimple, magnifyingGlass } from '@harbor/elements/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { checkCiscoProductsLoaded } from '../../support/slices/productsSlice';
import { RootState, useAppDispatch } from '../../support/store';
import { ProductFeature } from '../../types/product';
import ProductIcon from './ProductIcon';
import Highlighter from 'react-highlight-words';
import { useFetchSingleProduct } from '../../hooks/product-hooks';
import NavHistoryTracker from '../../navigation/NavHistoryTracker';
import StbAccordion from '../security-toolbox-ui/StbAccordion';
import { asSingleValue } from '../../support/utils';

const style = {
  propTitle: ['text-xs', 'font-semibold', 'uppercase', 'text-gray-50'].join(
    ' '
  ),
  h3: ['text-cisco-blue-80', 'font-semibold'].join(' '),
};

const ProductInfoItem = ({ name, value }) => {
  if (!value) {
    return;
  }
  return (
    <>
      <dd className={style.propTitle}>{name}</dd>
      <dt className='truncate font-semibold border-b border-gray-15 pb-2 mb-2 text-lg'>
        {value}
      </dt>
    </>
  );
};

const ProductLinkItem = ({
  displayText,
  url,
}: {
  displayText: string;
  url?: string;
}) => {
  if (!url) {
    return;
  }
  return (
    <li>
      <Link
        target='_blank'
        to={url}
        className='block transition-colors rounded-lg bg-med-blue-5 border border-med-blue-10 hover:bg-med-blue-10 hover:border-med-blue-20 text-lg px-2 py-px'
      >
        <HbrIcon
          asset={arrowSquareOut}
          size='12px'
          className='text-med-blue-60 mr-1'
        />
        <span slot='content' className='text-med-blue-60 text-base font-normal'>
          {displayText}
        </span>
      </Link>
    </li>
  );
};

const FeatureCard = ({ feature, search }: { feature: ProductFeature, search?: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className='px-4 py-2 rounded-lg border border-gray-20 bg-gray-10 cursor-pointer group relative'
      onClick={() => setIsOpen(!isOpen)}
    >
      <HbrIcon
        asset={arrowsOutSimple}
        className='absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-gray-60'
      />
      <h4 className={`my-1 leading-5 flex`}>
        <Highlighter
          highlightClassName='font-semibold p-px -m-px rounded-sm bg-warning-lighter'
          searchWords={[search]}
          autoEscape={true}
          textToHighlight={feature?.name ?? ''}
        />
      </h4>
      <p
        className={`text-sm leading-5 my-1 ${isOpen ? '' : 'line-clamp-3'}`}
        title={isOpen ? undefined : feature.description}
      >
        {feature.description}
      </p>
    </div>
  );
};

const ProductDetail = () => {
  const { id } = useParams();
  const idInt: number = parseInt(id);
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(checkCiscoProductsLoaded());
  }, [dispatch]);

  const ciscoProductsMap = useSelector((state: RootState) => state.products.ciscoProductMap ?? null);
  const product = useMemo(() => {
    if (ciscoProductsMap && Object.keys(ciscoProductsMap).length > 0) {
      return ciscoProductsMap[idInt] ?? null;
    }
    return null;
  }, [ciscoProductsMap, idInt]);

  const { data: detailedProduct, isLoading: isLoadingDetail } = useFetchSingleProduct(idInt);

  const [search, setSearch] = useState('');

  const featurePanels = {
    component: {
      id: 'component',
      title: 'Features by Component',
    },
    useCase: {
      id: 'useCase',
      title: 'Features by Use Case',
    },
    all: {
      id: 'all',
      title: 'All Features',
    },
  };

  const [featureDisplay, setFeatureDisplay] = useState<string>(
    featurePanels.component.id
  );

  const setFeatureTab = (tabId: string): void => {
    setSearch('');
    setFeatureDisplay(tabId);
  }

  const handleSearchChange = (value: string) => {
    setSearch(value.toLowerCase());
  };

  const filteredFeatures = useMemo(() => {
    const allFeatures = product?.feature ?? [];
    if (search.length) {
      return (allFeatures).filter(
        (feature) =>
          (feature?.name?.toLocaleLowerCase() ?? '').indexOf(search) > -1
      );
    } else {
      return allFeatures;
    }
  }, [search, product]);

  // not applicable for services or saas
  const showVersions = (): boolean =>
    !!product.version?.length &&
    // If not string numerics, might have incorrect offer_type
    !!product.version.every((v) => !!parseInt(v.version.split('.')[0])) &&
    ['hybrid_product', 'appliance_product'].includes(product.offer_type);

  const compareVersions = (v1: string, v2: string): number => {
    return v2.localeCompare(v1, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  // most recent 6 versions, sort by newest to oldest
  const sortedVersions = [...(product?.version ?? [])]
    .sort((v1, v2) => compareVersions(v1.version, v2.version))
    .slice(0, 6);

  const isRecommendedVersion = (version: string): boolean => {
    if (product.recommended_version) {
      return product.recommended_version === version;
    }
    // use most recent if we don't have recommended
    const newestVersion = product.version
      .map((v) => v.version)
      .sort(compareVersions)[0];
    return newestVersion === version;
  };

  return (
    <section className='mx-4 mt-2'>
      {!!product && (
        <>
          <NavHistoryTracker title={product.name} />
          <header className='my-4 flex gap-2 items-center'>
            <ProductIcon svgIconId={product.svg_icon} />
            <div>
              <h4 className='mt-0 mb-0 text-cisco-blue-60 leading-6'>
                Product
              </h4>
              <h1 className='mt-0 mb-2 text-gray-60 font-semibold leading-5'>
                {product.name}
              </h1>
            </div>
          </header>
          <div>
            <p>{product.description}</p>
          </div>
          <div className='mt-2 grid grid-cols-1 xl:grid-cols-[1fr_480px] gap-x-6 max-w-screen-2xl'>
            {!!product?.feature?.length && (
              <div>
                <HbrTabGroup primary>
                  {Object.keys(featurePanels).map((panelId) => (
                    <HbrTab
                      slot='nav'
                      key={panelId}
                      panel={panelId}
                      active={featureDisplay === panelId}
                      onClick={() => setFeatureTab(panelId)}
                    >
                      {featurePanels[panelId].title}
                    </HbrTab>
                  ))}
                  <HbrTabPanel name='component'>
                    {isLoadingDetail && (
                      <HbrSpinner size="medium" className="my-4" />
                    )}
                    {!isLoadingDetail && (detailedProduct?.component ?? []).map((component) => (
                      <section
                        className='my-4'
                        key={component.id}
                      >
                        <StbAccordion
                          headerContent={
                            <div className="flex flex-col w-full pr-2">
                              <div className="flex items-center justify-between">
                                <h3 className={style.h3}>{component.name}</h3>
                                <HbrBadge
                                  slot='suffix'
                                  pill={true}
                                  size='medium'
                                  sentiment={(component?.product_feature ?? []).length ? 'info' : 'neutral'}
                                  className='-my-2'
                                >
                                  {(component?.product_feature ?? []).length}
                                </HbrBadge>
                              </div>
                              <p className='mb-2'>{component.description}</p>
                            </div>
                          }
                        >
                          <div className='grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3 mx-4 mb-4'>
                            {(component?.product_feature ?? []).map((feature) => (
                              <FeatureCard feature={feature} key={feature.id} />
                            ))}
                          </div>
                        </StbAccordion>
                      </section>
                    ))}
                  </HbrTabPanel>
                  <HbrTabPanel name='useCase'>
                    {isLoadingDetail && (
                      <HbrSpinner size="medium" className="my-4" />
                    )}
                    {!isLoadingDetail && (detailedProduct?.use_case ?? []).map((useCase) => (
                      <section
                        className='my-4'
                        key={useCase.id}
                      >
                        <StbAccordion
                          headerContent={
                            <div className="flex flex-col w-full pr-2">
                              <div className="flex items-center justify-between">
                                <h3 className={style.h3}>{useCase.name}</h3>
                                <HbrBadge
                                  slot='suffix'
                                  pill={true}
                                  size='medium'
                                  sentiment={(useCase?.product_feature ?? []).length ? 'info' : 'neutral'}
                                  className='-my-2'
                                >
                                  {(useCase?.product_feature ?? []).length}
                                </HbrBadge>
                              </div>
                              <p className='mb-2'>{useCase.description}</p>
                            </div>
                          }
                        >
                          <div className='grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3 mx-4 mb-4'>
                            {(useCase.product_feature ?? []).map((feature) => (
                              <FeatureCard feature={feature} key={feature.id} />
                            ))}
                          </div>
                        </StbAccordion>
                      </section>
                    ))}
                  </HbrTabPanel>
                  <HbrTabPanel name='all'>
                    <div className='flex mt-2'>
                      <HbrInput
                        placeholder='Search Features'
                        value={search}
                        clearable={true}
                        onHbr-change={(e) => handleSearchChange(asSingleValue(e.target.value))}
                        className=''
                      >
                        <HbrIcon
                          slot='prefix'
                          name='magnifying-glass'
                          asset={magnifyingGlass}
                          size='16px'
                          staticColors={true}
                        />
                      </HbrInput>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3 mt-4 mb-8'>
                      {filteredFeatures.map((feature) => (
                        <FeatureCard feature={feature} key={feature.id} search={search} />
                      ))}
                    </div>
                  </HbrTabPanel>
                </HbrTabGroup>
              </div>
            )}
            <div>
              <h3 className={style.h3}>Information</h3>
              <dl className='border-t border-gray-15 pt-2'>
                <ProductInfoItem
                  name='Links'
                  value={
                    <ul className='flex flex-wrap gap-2 my-1'>
                      <ProductLinkItem
                        displayText='Sales: Product'
                        url={product.sales_url}
                      />
                      <ProductLinkItem
                        displayText='Sales Connect'
                        url={product.sales_connect_url}
                      />
                    </ul>
                  }
                />
              </dl>
              {showVersions() && (
                <div className='border-gray-15 border-b grid grid-cols-[min-content_6em_6em_1fr] gap-x-3 gap-y-1 mb-2 pb-2'>
                  <div className={style.propTitle}>Version</div>
                  <div className={style.propTitle}>Released</div>
                  <div className={style.propTitle}>Updated</div>
                  <div className={style.propTitle}>Documentation</div>
                  {sortedVersions.map((v) => (
                    <div
                      key={v.id}
                      className={`grid col-span-4 grid-cols-subgrid gap-y-0 ${
                        isRecommendedVersion(v.version)
                          ? '-mx-2 px-2 -mb-1 py-1 bg-green-10 border border-green-30 text-black rounded-md font-semibold'
                          : ''
                      }`}
                    >
                      {product.recommended_version === v.version && (
                        <div
                          className={`col-span-4 font-light text-center ${style.propTitle} text-green-60`}
                        >
                          Recommended
                        </div>
                      )}
                      <div className='font-semibold text-xl truncate'>
                        {v.version}
                      </div>
                      <div className='h-full flex items-center'>
                        {v.released?.toLocaleString()}
                      </div>
                      <div className='h-full flex items-center'>
                        {v.last_update?.toLocaleString()}
                      </div>
                      <div className='h-full flex font-normal'>
                        {product.recommended_version === v.version && (
                          <>
                            <Link
                              className='mr-2 flex items-center hover:underline'
                              target='_blank'
                              to={v.release_notes_url}
                            >
                              Notes
                            </Link>
                            <Link
                              className='mr-2 flex items-center hover:underline'
                              target='_blank'
                              to={v.configuration_guide}
                            >
                              Config
                            </Link>
                            <Link
                              className='mr-2 flex items-center hover:underline'
                              target='_blank'
                              to={v.api_documentation}
                            >
                              Api
                            </Link>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <dl className='pt-px'>
                <ProductInfoItem
                  name='Firestarter'
                  value={
                    <ul className='flex flex-wrap gap-2 my-1'>
                      <ProductLinkItem
                        displayText='POV'
                        url={product.firestarter_pov}
                      />
                      <ProductLinkItem
                        displayText='TME'
                        url={product.firestarter_tme}
                      />
                      <ProductLinkItem
                        displayText='Presentation'
                        url={product.firestarter_presentation}
                      />
                      <ProductLinkItem
                        displayText='Automation'
                        url={product.firestarter_automation}
                      />
                      <ProductLinkItem
                        displayText='Feature'
                        url={product.firestarter_feature}
                      />
                    </ul>
                  }
                />
                <ProductInfoItem
                  name='SF Technology'
                  value={product.sf_technology}
                />
                <ProductInfoItem
                  name='STC Trial Available'
                  value={product.stc_trial_available ? 'Yes' : 'No'}
                />
              </dl>
              {!!product.appliance?.length && (
                <>
                  <h3 className={style.h3}>Appliance</h3>
                  <ul>
                    {product.appliance?.map((a) => (
                      <li key={a.id}>
                        <span className='font-semibold'>{a.name}</span>{' '}
                        {a.description}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <h3 className={style.h3}>Achitecture</h3>
              <ul>
                {product.architecture?.map((a) => (
                  <li key={a.id}>
                    <span className='font-semibold'>{a.name}</span>{' '}
                    {a.description}
                  </li>
                ))}
              </ul>
              <h3 className={style.h3}>License</h3>
              <ul>
                {product.license?.map((a) => (
                  <li key={a.id}>
                    <span className='font-semibold'>{a.name}</span>{' '}
                    {a.consumption_model}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
      {!product && (
        <header className='my-4'>
          <h4 className='mt-0 mb-0 text-red-50'>Error</h4>
          <h1 className='my-0 text-gray-60 font-semibold'>
            Product ID {id} not found
          </h1>
        </header>
      )}
    </section>
  );
};

export default ProductDetail;
